import { defineStore } from 'pinia';
import type { identifier } from '@core/models/base';
import { isServiceError, isServiceSuccess } from '@core/models/actionResponse';
import type { OrderDisplayModel } from '@shared/models/orders/order';
import { STUDIO_SHIPPING } from '@shared/models/shippingOptions';
import type { CartImageDisplayModel } from '@shared/models/carts/cartImages';
import type { CheckoutClientRequestModel, CheckoutServerModel, CheckoutTotals } from '../models/checkout';
import {
  fetchCheckoutState,
  fetchCheckoutTotals,
  fetchCheckoutOrderModel,
  saveCheckoutData,
} from '../services/checkout.service';
import { useCustomerStore } from '@apps/customer/stores/customer.store';
import { useCartStore } from '@apps/customer/stores/cart.store';

export interface ICheckoutState {
  checkoutDetails?: CheckoutServerModel;
  clientModel?: CheckoutClientRequestModel;
  totals?: CheckoutTotals;
  orderDetails?: OrderDisplayModel;
  cartImages?: CartImageDisplayModel[];
}
export const useCheckoutStore = defineStore('checkoutStore', {
  state: (): ICheckoutState => ({
    checkoutDetails: undefined,
    clientModel: undefined,
    totals: undefined,
    orderDetails: undefined,
    cartImages: undefined,
  }),
  actions: {
    async load(jobId: identifier, force = false) {
      if (this.checkoutDetails && !force) return;
      const result = await fetchCheckoutState(jobId);
      if (isServiceError(result)) return result;

      this.checkoutDetails = result.data;
      // customer will always be set.  Use this to determine existence of checkout state
      if (this.checkoutDetails.customer) {
        this.clientModel = {
          billingAddress: this.checkoutDetails.billingAddress,
          customer: this.checkoutDetails.customer,
          shippingAddressSameAsBillingAddress: this.checkoutDetails.shippingAddressSameAsBillingAddress,
          shippingAddress: this.checkoutDetails.shippingAddress,
          payMethod: this.checkoutDetails.payMethod,
          shippingMethod: this.checkoutDetails.isPartOfBatchOrder
            ? STUDIO_SHIPPING
            : this.checkoutDetails.shippingOption?.code,
          recreatePaymethod: false,
          isPartOfBatchOrder: this.checkoutDetails.isPartOfBatchOrder,
          promoCode: this.checkoutDetails.promoCode?.code,
          paymentToken: '',
          mailToHome: this.checkoutDetails.mailToHome,
          orderMethod: this.checkoutDetails.orderMethod,
          jobVersion: this.checkoutDetails.jobVersion,
          isV3Job: this.checkoutDetails.isV3Job,
        };
      }
    },
    async postCheckoutDetails(jobId: identifier, model: CheckoutClientRequestModel) {
      const customerStore = useCustomerStore();
      const cartStore = useCartStore();

      model.orderMethod = cartStore.persistedOrderMethod ?? 'Unknown';
      model.jobVersion = customerStore.job?.version;
      model.isV3Job = (customerStore.job?.version?.major ?? 0) >= 3;

      const result = await saveCheckoutData(jobId, model);
      if (isServiceSuccess(result)) this.checkoutDetails = result.data;
      return result;
    },

    async loadTotals(jobId: identifier) {
      const result = await fetchCheckoutTotals(jobId);
      if (isServiceSuccess(result)) this.totals = result.data;
    },
    async loadOrderDetails(jobId: identifier, orderNumber: string) {
      const result = await fetchCheckoutOrderModel(jobId, orderNumber);
      if (isServiceSuccess(result)) this.orderDetails = result.data;
      return result;
    },
    async clear() {
      this.checkoutDetails = undefined;
      this.clientModel = undefined;
      this.clearOrder();
    },
    async clearOrder() {
      this.orderDetails = undefined;
      this.totals = undefined;
    },
    async clearImages() {
      this.cartImages = undefined;
    },
  },
});
